import { Button } from "components/ui/Button";
import type { FC } from "react";

type MatchingResultsPaginationProps = {
  page: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
};

/**
 * マッチング結果ページネーション
 * @param {MatchingResultsPaginationProps} props コンポーネントProps
 * @return {JSX.Element} MatchingResultsPagination コンポーネント
 */
export const MatchingResultsPagination: FC<MatchingResultsPaginationProps> = ({
  page,
  total,
  limit,
  onPageChange,
}) => {
  const totalPages = Math.ceil(total / limit);

  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | "start-ellipsis" | "end-ellipsis")[] => {
    const pages: (number | "start-ellipsis" | "end-ellipsis")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("start-ellipsis");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("end-ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-base-content/60">
        全{total}件中 {(page - 1) * limit + 1}〜{Math.min(page * limit, total)}
        件
      </span>
      <div className="join">
        <Button
          type="button"
          variant="neutral"
          size="sm"
          isJoinItem
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
        >
          前へ
        </Button>
        {getPageNumbers().map(p =>
          typeof p === "string" ? (
            <Button
              type="button"
              key={p}
              variant="neutral"
              size="sm"
              isJoinItem
              disabled
            >
              ...
            </Button>
          ) : (
            <Button
              type="button"
              key={p}
              variant="neutral"
              size="sm"
              isJoinItem
              isActive={p === page}
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          type="button"
          variant="neutral"
          size="sm"
          isJoinItem
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          次へ
        </Button>
      </div>
    </div>
  );
};
